import React, { FunctionComponent, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { APIContext } from 'src/contexts/api';
import { logError } from 'src/utils/Error';
import { AxiosError, AxiosResponse } from 'axios'
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import useFieldStyles from 'src/assets/css/components/ParameterFieldStyles';

/*
 * Custom Data Plugin field for Xero 'tap-xero.tenant_id'
 *
 * Fetches and displays a list of tenants from Xero connections for the user to choose.
 */

const XeroTenantIdField: FunctionComponent<any> = (props: PropsWithChildren<any>) => {
  const {
    properties,
    setting,
    prefix,
    handleSettingsChange,
    handleSettingsChangeEvent,
    handleSettingsUpdate,
    currentValue,
    error,
  } = props;

  const propertyName = prefix ? `${prefix}.${setting.name}` : setting.name;
  const accessToken =
    properties[prefix ? `${prefix}.oauth_credentials.access_token` : 'oauth_credentials.access_token'];
  const refreshToken =
    properties[prefix ? `${prefix}.oauth_credentials.refresh_token` : 'oauth_credentials.refresh_token'];
  const { label, required } = setting;
  const { catalogAPI } = useContext(APIContext);
  const classes = useFieldStyles();
  const isRequired = required == undefined ? true : required.toString().toLowerCase() !== 'false';
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentOption, setCurrentOption] = useState<any>(null);

  const settingsChange = (event: any, newValue: any) => {
    handleSettingsChangeEvent ? handleSettingsChangeEvent(event) : null;
    handleSettingsChange(propertyName, newValue ? newValue.tenantId : null);
  };

  /*
   * Refresh the access_token used to GET Xero connections.
   */
  const refreshAccessToken = async (token: any) => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    const exchangeBody = {
      grant_type: 'refresh_token',
      refresh_token: token,
    };

    await catalogAPI
      .post(encodeURI(`/tokens/oauth2-xero/token`), exchangeBody, { errorHandler: false })
      .then((resp: AxiosResponse) => {
        const data = resp.data;
        properties[prefix ? `${prefix}.oauth_credentials.access_token` : 'oauth_credentials.access_token'] =
          data.access_token;
        handleSettingsUpdate?.(properties);
        setIsLoading(true);
      })
      .catch((e: AxiosError) => {
        logError(e);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    let active = true;

    if (!isLoading || !accessToken) {
      return;
    }

    /*
     * Use our proxy as xero api does not support CORS
     */
    (async () => {
      catalogAPI
        .get(encodeURI('/proxy/proxy-xero/connections'), {
          errorHandler: false,
          headers: {
            'X-Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        })
        .then((resp:AxiosResponse) => {
          if (!active) return;
          const tenants = resp.data || [];
          setOptions(tenants);
          setIsLoading(false);
        })
      .catch((error: AxiosError) => {
          if (error.response && error.response.status === 401) {
            refreshAccessToken(refreshToken);
          } else {
            logError(error);
          }
        });
    })();

    return () => {
      active = false;
    };
  }, [isLoading, accessToken, refreshToken]);

  useEffect(() => {
    const selectedOption = options.find((o: any) => o.tenantId === currentValue);
    setCurrentOption(selectedOption || null);
  }, [options, currentValue]);

  const input = (
    <Autocomplete
      data-testid="tap-xero.tenant_id"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={settingsChange}
      getOptionSelected={(option: any, value: any) => !!option && !!value && option.tenantId === value.tenantId}
      getOptionLabel={(option: any) => (option ? option.tenantName || option.tenantId || '' : '')}
      options={isLoading ? [] : options}
      loading={isLoading}
      ListboxProps={{ 'data-testid': 'tap-xero.tenants' }}
      value={currentOption}
      renderInput={(params) => (
        <TextField
          {...params}
          error={!!error}
          helperText={error?.defaultMessage || ''}
          label={label}
          name={propertyName}
          variant="outlined"
          required={isRequired}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );

  return <div className={classes.root}>{input}</div>;
};

export default XeroTenantIdField;
